import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  InputBase,
  Box,
  Typography,
  Avatar,
  CircularProgress,
  ClickAwayListener,
  Paper,
} from '@mui/material';
import { SearchContainer, SearchBarStyled } from './widgets.styles';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { useNavigate } from 'react-router-dom';
import { usersService } from '../../../../services/service-proxy';
import { getInitials } from '../../../../utils/get-initials.util';
import WhoToFollow from './components/who-to-follow/who-to-follow.component';
import TodayStats from './components/today-stats/today-stats.component';
import TrendingWorkouts from './components/trending-workouts/trending-workouts.component';
import ActiveStreak from './components/active-streak/active-streak.component';

const { searchUsers } = usersService;

/**
 * Search results dropdown component.
 */
const SearchResults = ({ results, loading, query, onSelect }) => {
  if (!query.trim()) return null;

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        zIndex: 100,
        maxHeight: 300,
        overflowY: 'auto',
        mt: 0.5,
        borderRadius: 2,
      }}
      role="listbox"
      aria-label="Search results"
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : results.length === 0 ? (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <SearchOffIcon sx={{ fontSize: 32, color: 'text.secondary', mb: 0.5 }} />
          <Typography variant="body2" color="text.secondary">
            No users found for &ldquo;{query}&rdquo;
          </Typography>
        </Box>
      ) : (
        results.map((user) => (
          <Box
            key={user.id}
            onClick={() => onSelect(user)}
            role="option"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(user);
              }
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.5,
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'action.hover' },
              '&:focus-visible': {
                backgroundColor: 'action.hover',
                outline: 'none',
              },
              transition: 'background-color 0.15s',
            }}
          >
            <Avatar
              src={user.avatarUrl || undefined}
              alt={`${user.displayName}'s avatar`}
              sx={{ width: 36, height: 36, fontSize: '0.875rem' }}
            >
              {getInitials(user.displayName || '')}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                {user.displayName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                @{user.username}
              </Typography>
            </Box>
          </Box>
        ))
      )}
    </Paper>
  );
};

const Widgets = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const debounceTimerRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(async () => {
      try {
        const results = await searchUsers(searchQuery, 8);
        setSearchResults(results);
      } catch (err) {
        setSearchResults([]);
      }
      setSearchLoading(false);
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
    setShowResults(true);
  }, []);

  const handleSelectUser = useCallback((user) => {
    setSearchQuery('');
    setShowResults(false);
    setSearchResults([]);
    navigate(`/profile/${user.username}`);
  }, [navigate]);

  const handleClickAway = useCallback(() => {
    setShowResults(false);
    setSearchFocused(false);
  }, []);

  const handleFocus = useCallback(() => {
    setSearchFocused(true);
    if (searchQuery.trim()) {
      setShowResults(true);
    }
  }, [searchQuery]);

  const handleBlur = useCallback(() => {
    setSearchFocused(false);
  }, []);

  return (
    <Box component="aside" aria-label="Sidebar widgets">
      {/* Search bar */}
      <ClickAwayListener onClickAway={handleClickAway}>
        <SearchContainer>
          <Box sx={{ position: 'relative', width: '100%' }}>
            <SearchBarStyled $focused={searchFocused}>
              <SearchIcon
                sx={{
                  fontSize: 20,
                  color: searchFocused ? 'primary.main' : 'text.secondary',
                  mr: 1,
                  transition: 'color 0.2s',
                }}
              />
              <InputBase
                placeholder="Search users..."
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                fullWidth
                inputProps={{
                  'aria-label': 'Search users',
                  role: 'combobox',
                  'aria-expanded': showResults && searchQuery.trim() ? 'true' : 'false',
                }}
                sx={{ fontSize: '0.875rem' }}
              />
            </SearchBarStyled>
            {showResults && searchQuery.trim() && (
              <SearchResults
                results={searchResults}
                loading={searchLoading}
                query={searchQuery}
                onSelect={handleSelectUser}
              />
            )}
          </Box>
        </SearchContainer>
      </ClickAwayListener>

      {/* Active Streak */}
      <ActiveStreak />

      {/* Today's Stats */}
      <TodayStats />

      {/* Who to Follow */}
      <WhoToFollow />

      {/* Trending Workouts */}
      <TrendingWorkouts />
    </Box>
  );
};

export default Widgets;
